/**
 * Tip — el tooltip de la aplicación.
 *
 * POR QUÉ EXISTE: la API cruda de Radix pide cuatro componentes anidados por
 * cada botón con ayuda. Escribir `title="Ver documento"` cuesta una palabra.
 * Cuando lo correcto cuesta cinco veces más que lo incorrecto, gana lo
 * incorrecto — y así terminamos con tooltips del navegador repartidos por toda
 * la app. `Tip` iguala el costo: una línea, igual que el atributo nativo.
 *
 *   <Tip texto="Ver documento">
 *     <Button size="icon-sm" aria-label="Ver documento"><Icon name="visibility" /></Button>
 *   </Tip>
 *
 * NO REEMPLAZA AL NOMBRE ACCESIBLE. Radix expone el tooltip como
 * `aria-describedby`, es decir una DESCRIPCIÓN, no un nombre. El navegador, en
 * cambio, usa `title` como nombre accesible de último recurso. Por eso un botón
 * de solo ícono necesita SIEMPRE su `aria-label` además del Tip: sin él, el
 * lector de pantalla anuncia "botón" y nada más.
 *
 * NO USAR PARA INFORMACIÓN IMPRESCINDIBLE. El tooltip no se abre con touch —
 * no hay hover en un teléfono. Lo que el usuario NECESITA leer para decidir va
 * en la pantalla, no acá.
 */
import * as React from "react";

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

export type LadoTip = "top" | "right" | "bottom" | "left";

export interface TipProps {
  /** Contenido de la ayuda. Si viene vacío, el hijo se renderiza tal cual. */
  texto?: React.ReactNode;
  /** El disparador. Un único elemento — recibe el hover y el foco. */
  children: React.ReactElement;
  /** Lado preferido. Radix lo corrige solo si no entra en pantalla. */
  lado?: LadoTip;
  alineacion?: "start" | "center" | "end";
  /** Apaga el tooltip sin sacar el `<Tip>` del árbol (útil con condicionales). */
  deshabilitado?: boolean;
  /** Ancho máximo del globo. Por defecto se limita para no cruzar la pantalla. */
  className?: string;
  /**
   * Clases para el span que envuelve a los disparadores que pueden apagarse
   * (ver `prepararDisparador`). Ese span pasa a ocupar el lugar del hijo en el
   * layout del padre, así que si el hijo traía `flex-none`, `w-full` o
   * similares, hay que repetirlas acá o la fila se descuadra.
   */
  claseEnvoltorio?: string;
  /**
   * Fuerza el span envolvente aunque el hijo no declare `disabled`. Hace falta
   * cuando el disparador es OTRO componente que reenvía props hacia abajo —
   * `<AlertDialogTrigger asChild>`, por ejemplo: el `disabled` real está en el
   * botón que ese trigger envuelve, así que desde acá no se ve.
   */
  envolver?: boolean;
}

/**
 * Un `<button disabled>` no emite eventos de puntero, así que Radix nunca se
 * entera del hover y el tooltip no abre — justo al revés que el `title` nativo,
 * que el navegador muestra igual. Para no perder la ayuda en los botones
 * apagados ("Próximamente", cupos insuficientes) envolvemos el disparador en un
 * span que sí recibe el evento, y le sacamos los eventos al botón para que lo
 * dejen pasar hacia el padre.
 *
 * La decisión de envolver se toma por la PRESENCIA de la prop `disabled`, no
 * por su valor: con `disabled={cargando}` el valor cambia en pleno uso, y
 * cambiar la forma del árbol desmontaría y volvería a montar el botón, con la
 * consiguiente pérdida del foco. La estructura queda fija; lo que cambia es
 * sólo cuál elemento gana el hit-test.
 *
 * Los `pointer-events` se apagan con un selector descendiente en vez de clonar
 * al hijo: así también alcanza al NIETO cuando el disparador es un
 * `<AlertDialogTrigger asChild>` y el `disabled` real está un nivel más abajo.
 */
function prepararDisparador(
  children: React.ReactElement,
  claseEnvoltorio?: string,
  forzarEnvoltorio?: boolean,
): React.ReactElement {
  const props = children.props as {
    disabled?: boolean;
    "aria-disabled"?: boolean | "true" | "false";
  };

  const puedeApagarse =
    props?.disabled !== undefined || props?.["aria-disabled"] !== undefined;
  if (!puedeApagarse && !forzarEnvoltorio) return children;

  const apagado =
    props?.disabled === true ||
    props?.["aria-disabled"] === true ||
    props?.["aria-disabled"] === "true";

  return (
    // Con el disparador apagado el span toma su lugar como parada de
    // tabulación, para que el tooltip también se alcance con el teclado.
    <span
      className={cn(
        "inline-flex [&_:disabled]:pointer-events-none",
        claseEnvoltorio,
      )}
      tabIndex={apagado ? 0 : undefined}
    >
      {children}
    </span>
  );
}

export function Tip({
  texto,
  children,
  lado = "top",
  alineacion = "center",
  deshabilitado = false,
  className,
  claseEnvoltorio,
  envolver,
}: TipProps) {
  if (deshabilitado || texto == null || texto === "") return children;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {prepararDisparador(children, claseEnvoltorio, envolver)}
      </TooltipTrigger>
      <TooltipContent
        side={lado}
        align={alineacion}
        className={cn("max-w-[280px]", className)}
      >
        {texto}
      </TooltipContent>
    </Tooltip>
  );
}

export interface TextoTruncadoProps
  extends React.ComponentPropsWithoutRef<"span"> {
  children: React.ReactNode;
  /** Qué mostrar en el globo. Por defecto, el mismo texto del hijo. */
  textoCompleto?: string;
  lado?: LadoTip;
}

/**
 * Texto de una línea que se corta con puntos suspensivos y ofrece el contenido
 * completo al pasar el mouse — pero SOLO si de verdad está cortado.
 *
 * Es la diferencia con el `title` nativo, que repite el texto aunque entre
 * entero y termina tapando lo que el usuario ya podía leer.
 */
export function TextoTruncado({
  children,
  textoCompleto,
  lado = "top",
  className,
  ...props
}: TextoTruncadoProps) {
  const ref = React.useRef<HTMLSpanElement>(null);
  const [abierto, setAbierto] = React.useState(false);
  const [cortado, setCortado] = React.useState(false);

  /**
   * Se mide en el instante en que Radix quiere abrir, no al montar.
   *
   * Medir en un efecto de montaje daba SIEMPRE "no cortado": a esa altura
   * todavía no bajó la tipografía Geist, así que el texto ocupa menos de lo
   * que va a ocupar. Y un ResizeObserver no rescata nada, porque observa la
   * caja del elemento —que tiene el ancho fijo que le da el padre y nunca
   * cambia—, no su contenido. El resultado era un tooltip que no aparecía
   * jamás.
   */
  const alCambiarApertura = (quiereAbrir: boolean) => {
    if (quiereAbrir) {
      const el = ref.current;
      setCortado(!!el && el.scrollWidth > el.clientWidth + 1);
    }
    setAbierto(quiereAbrir);
  };

  const completo =
    textoCompleto ?? (typeof children === "string" ? children : undefined);

  return (
    <Tooltip open={abierto && cortado && !!completo} onOpenChange={alCambiarApertura}>
      <TooltipTrigger asChild>
        <span ref={ref} className={cn("block truncate", className)} {...props}>
          {children}
        </span>
      </TooltipTrigger>
      <TooltipContent side={lado} className="max-w-[280px]">
        {completo}
      </TooltipContent>
    </Tooltip>
  );
}
